import React, { useContext, useState } from 'react';
import { Button, Modal, Upload, message, Flex, Typography, Input, Switch } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import { DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import uploadCompanyFiles from '../../../../domain/use_cases/companies/uploadCompanyFiles';
import AuthenticationContext from '../../../../presentation/contexts/auth/AuthenticationContext';

interface FileMeta {
    name: string;
    description: string;
    sensitive: boolean;
}

interface CompanyFileUploadModalProps {
    open: boolean;
    onCancel: () => void;
    companyId: string;
    onUpdateCallback: () => void;
    maxDescriptionLength?: number;
}

export default function CompanyFileUploadModal({ open, onCancel, companyId, onUpdateCallback, maxDescriptionLength = 50 }: CompanyFileUploadModalProps) {
    const authContext = useContext(AuthenticationContext);

    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [fileMeta, setFileMeta] = useState<Record<string, FileMeta>>({});
    const [uploading, setUploading] = useState(false);

    const handleUpload = async () => {
        const entries = fileList
            .filter((f) => f.originFileObj)
            .map((f) => ({
                file: f.originFileObj as File,
                meta: fileMeta[f.uid] ?? { name: f.name, description: '', sensitive: false },
            }));
        if (entries.length === 0) return;

        setUploading(true);
        try {
            await uploadCompanyFiles(
                companyId,
                entries.map((e) => e.file),
                entries.map((e) => e.meta),
                authContext.authentication
            );
            message.success('Fichiers téléversés avec succès');
            closeUploadModal();
            onUpdateCallback();
        } catch (e) {
            message.error(e instanceof Error ? e.message : 'Erreur lors du téléversement');
        } finally {
            setUploading(false);
        }
    };

    const handleFileChange: UploadProps['onChange'] = ({ fileList: newList }) => {
        setFileList(newList);
        setFileMeta((prev) => {
            const next: Record<string, FileMeta> = {};
            newList.forEach((f) => {
                next[f.uid] = prev[f.uid] ?? { name: f.name, description: '', sensitive: false };
            });
            return next;
        });
    };


    const updateFileMeta = (uid: string, patch: Partial<FileMeta>) => {
        setFileMeta((prev) => ({ ...prev, [uid]: { ...prev[uid], ...patch } }));
    };

    const handleRemoveFile = (uid: string) => {
        setFileList((prev) => prev.filter((f) => f.uid !== uid));
        setFileMeta((prev) => {
            const next = { ...prev };
            delete next[uid];
            return next;
        });
    };

    const closeUploadModal = () => {
        setFileList([]);
        setFileMeta({});
        onCancel();
    };

    return <Modal
        title="Téléverser des fichiers"
        open={open}
        onOk={handleUpload}
        onCancel={onCancel}
        okText="Téléverser"
        cancelText="Annuler"
        confirmLoading={uploading}
        okButtonProps={{ disabled: fileList.length === 0 }}
        destroyOnHidden
    >
        <Upload.Dragger
            name="files"
            multiple
            fileList={fileList}
            showUploadList={false}
            beforeUpload={() => false}
            onChange={handleFileChange}
            maxCount={10}
        >
            <p className="ant-upload-drag-icon">
                <InboxOutlined />
            </p>
            <p className="ant-upload-text">
                Cliquez ou glissez des fichiers dans cette zone
            </p>
        </Upload.Dragger>

        {fileList.length > 0 && (
            <Flex vertical gap="middle" style={{ marginTop: 16, maxHeight: '50vh', overflowY: 'auto' }}>
                {fileList.map((f) => (
                    <div key={f.uid} style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12 }}>
                        <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
                            <Typography.Text type="secondary" ellipsis>{f.name}</Typography.Text>
                            <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleRemoveFile(f.uid)} />
                        </Flex>
                        <Input
                            placeholder="Nom du fichier"
                            value={fileMeta[f.uid]?.name}
                            onChange={(e) => updateFileMeta(f.uid, { name: e.target.value })}
                            style={{ marginBottom: 8 }}
                        />
                        <Input
                            placeholder="Description"
                            maxLength={maxDescriptionLength}
                            showCount
                            value={fileMeta[f.uid]?.description}
                            onChange={(e) => updateFileMeta(f.uid, { description: e.target.value })}
                            style={{ marginBottom: 8 }}
                        />
                        <Flex align="center" gap="small">
                            <Switch
                                checked={fileMeta[f.uid]?.sensitive}
                                onChange={(checked) => updateFileMeta(f.uid, { sensitive: checked })}
                            />
                            <Typography.Text>Sensible</Typography.Text>
                        </Flex>
                    </div>
                ))}
            </Flex>
        )}
    </Modal>;
}
